import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface GroupButtonOption<T = string | number> {
	label?: string;
	labelKey?: string;
	value: T;
}

@Component({
	selector: 'app-group-button',
	templateUrl: './group-button.component.html',
	styleUrls: ['./group-button.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => GroupButtonComponent),
			multi: true
		}
	]
})
export class GroupButtonComponent<T = string | number> implements ControlValueAccessor {
	@Input() options: GroupButtonOption<T>[] = [];
	@Input() disabled = false;
	@Input() isVariant = false;

	public value: T | null = null;

	private onChange: (value: T | null) => void = () => {};
	private onTouched: () => void = () => {};

	constructor(private readonly cdr: ChangeDetectorRef) {}

	writeValue(value: T | null): void {
		this.value = value ?? null;
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: T | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	selectOption(value: T): void {
		if (this.disabled) {
			return;
		}

		this.value = value;
		this.onChange(this.value);
		this.onTouched();
		this.cdr.markForCheck();
	}

	isSelected(value: T): boolean {
		return this.value === value;
	}

	trackByValue(_: number, option: GroupButtonOption<T>): T {
		return option.value;
	}

	isFirst(value: T): boolean {
		return this.options.findIndex(option => option.value === value) === 0;
	}
}
